import { useState } from 'react';
import { Surcharge, SurchargeCreate, SurchargeUpdate } from '../types/costwand.d';
import { useSurcharges } from '../hooks/useSurcharges.ts';
import SurchargeForm from '../components/SurchargeForm.tsx';
import SurchargeTable from '../components/SurchargeTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const SurchargesPage = () => {
  const {
    surcharges,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createSurcharge,
    updateSurcharge,
    deleteSurcharge,
    fetchSurcharges,
  } = useSurcharges();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingSurcharge, setEditingSurcharge] = useState<Surcharge | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddSurcharge = () => {
    setEditingSurcharge(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditSurcharge = (surcharge: Surcharge) => {
    setEditingSurcharge(surcharge);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteSurcharge = async (id: number) => {
    await deleteSurcharge(id);
    fetchSurcharges(currentPage);
  };

  const handleFormSubmit = async (data: SurchargeCreate | SurchargeUpdate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingSurcharge) {
        success = await updateSurcharge(editingSurcharge.id!, data as SurchargeUpdate);
      } else {
        success = await createSurcharge(data as SurchargeCreate);
      }

      if (success) {
        setIsFormOpen(false);
        fetchSurcharges(currentPage);
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingSurcharge(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Surcharge Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddSurcharge}>Add New Surcharge</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingSurcharge ? 'Edit Surcharge' : 'Add New Surcharge'}</DialogTitle>
              <DialogDescription>
                {editingSurcharge ? 'Make changes to the surcharge here.' : 'Add a new surcharge to your list.'}
              </DialogDescription>
            </DialogHeader>
            <SurchargeForm
              initialData={editingSurcharge}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <SurchargeTable
        surcharges={surcharges}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditSurcharge}
        onDelete={handleDeleteSurcharge}
        loading={loading}
        error={error}
        baseEndpoint="/surcharges" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default SurchargesPage;